/**
 * Partners whose tracking URL must never appear in served HTML, JSON or email.
 *
 * A raw tracking href is followed by crawlers, link checkers and email
 * security scanners straight to the partner, where every fetch books a click
 * we cannot filter. 90 days to 2026-10-07: PartnerStack counted 119,336 Deel
 * clicks against our 1,271 person clicks, 0 signups, and the Network declined
 * the account as "Association with potentially fraudulent accounts". Linking
 * through `/<locale>/go/<slug>` puts our bot gate (204 for bots, our own page
 * for hops no person clicked) between every fetch and the partner.
 *
 * Only single-link providers belong here: the go-route resolves the slug to
 * one target, so a multi-item grid (Shopee) cannot ride it.
 */
export const GO_LINK_PROVIDERS: ReadonlySet<string> = new Set([
	"deel",
	"remote",
	"wise",
]);

/** Whether this provider's links must go through the go-route. */
export function usesGoLink(provider: string): boolean {
	return GO_LINK_PROVIDERS.has(provider);
}

/**
 * Go-route slug for an ENGINE-served offer (OfferCard, the sticky bar,
 * post-apply), or undefined when the offer has no link to route.
 *
 * Every engine offer goes through the go-route, not just `GO_LINK_PROVIDERS`:
 * the route resolves the slug through the same registry the offer route used
 * (code providers + `affiliate_offers` rows + the `links` override) and
 * redirects to `href ?? items[0].href` — exactly the link these surfaces
 * render. Before 2026-10-30 only deel/remote/wise did, so the sticky bar and
 * every DB partner (huntr, contra, fiverr, …) linked the raw partner URL: a
 * click our ledger only saw if the client beacon survived, and a URL every
 * crawler that renders the page could follow. Partners counted Huntr 822x
 * and Reditus 140x our clicks (scripts/research/traffic-yield-2026-11 §7).
 *
 * `verbatim` is the contract escape hatch (`PinnedOffer`, the Deel challenge
 * post): the exact partner URL must stay in the HTML.
 */
export function offerGoSlug(
	offer: {
		provider: string;
		href?: string;
		items?: ReadonlyArray<{ href: string }>;
	},
	verbatim = false,
): string | undefined {
	if (verbatim) return undefined;
	const href = offer.href ?? offer.items?.[0]?.href;
	return href ? offer.provider : undefined;
}

/**
 * Same-origin go-link for an on-site CTA. `surface` and `cid` let the
 * server-written click join the impression that minted the id (the Hunt
 * sponsored card pattern) — the caller must then skip its own client click
 * write, or the click counts twice.
 */
export function goLinkHref(
	slug: string,
	locale: string,
	opts: {
		surface?: string;
		cid?: string;
		/** Context-bandit bucket (`intent|catGroup|geo`), written to the click row. */
		cb?: string;
		/** Copy-variant id of the clicked card, written to the click row. */
		variant?: string;
		/** Context the route needs to rebuild a per-context link (VisualCV
		 * `sid`, the Educative course map). */
		jobId?: string | null;
		category?: string | null;
	} = {},
): string {
	const params = new URLSearchParams();
	if (opts.surface) params.set("surface", opts.surface);
	if (opts.cid) params.set("cid", opts.cid);
	if (opts.cb) params.set("cb", opts.cb);
	if (opts.variant) params.set("v", opts.variant);
	if (opts.jobId) params.set("jobId", opts.jobId);
	if (opts.category) params.set("category", opts.category);
	const query = params.toString();
	return `/${locale}/go/${encodeURIComponent(slug)}${query ? `?${query}` : ""}`;
}

/**
 * Absolute go-link for off-site surfaces (email, social). `src` labels the
 * click's surface, like the email footer sponsor's `?src=email_footer`.
 */
export function absoluteGoLink(
	baseUrl: string,
	slug: string,
	locale: string,
	src: string,
): string {
	const url = new URL(`/${locale}/go/${encodeURIComponent(slug)}`, baseUrl);
	url.searchParams.set("src", src);
	return url.toString();
}
